import React, { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, StaticRouter } from 'react-router';
import './App.css';
import { Toaster } from './components/ui/toaster';
import Layout from './components/Layout';
import RequestDemoPage from './pages/RequestDemoPage';
import HomePage from './pages/HomePage';
import BriefingIQPage from './pages/BriefingIQPage';
import EventIQPage from './pages/EventIQPage';
import PlatformPage from './pages/PlatformPage';
import CompanyPage from './pages/CompanyPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminBlog from './pages/admin/AdminBlog';
import AdminBlogEditor from './pages/admin/AdminBlogEditor';
import AdminImages from './pages/admin/AdminImages';
import AdminContacts from './pages/admin/AdminContacts';
import AdminWorkflows from './pages/admin/AdminWorkflows';
import AdminWorkflowNew from './pages/admin/AdminWorkflowNew';
import AdminWorkflow from './pages/admin/workflow/AdminWorkflow';
import ProtectedRoute from './pages/admin/ProtectedRoute';
import AnalyticsTracker from './components/AnalyticsTracker';
import CookieConsent from './components/CookieConsent';
import HardNavigation from './components/HardNavigation';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import { track } from './lib/analytics';

// `location` + `helmetContext` are only passed by the build-time prerenderer (scripts/prerender.js)
function App({ location, helmetContext }) {
  const handleOpenDemo = (e) => {
    const el = e?.currentTarget;
    const where = el?.dataset?.cta || (el?.closest?.('header') ? 'header' : el?.closest?.('footer') ? 'footer' : 'body');
    track('demo_cta_click', { label: where, cta_location: where });
    window.location.assign('/request-demo'); // full page load (see HardNavigation)
  };

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  const Router = location ? StaticRouter : BrowserRouter;
  const routerProps = location ? { location } : {};

  return (
    <HelmetProvider context={helmetContext}>
      <div className="App">
        <Router {...routerProps}>
          <HardNavigation />
          <AnalyticsTracker />
          <CookieConsent />
          <Routes>
            {/* Public site with layout */}
            <Route element={<Layout onOpenDemo={handleOpenDemo} />}>
              <Route path="/" element={<HomePage onOpenDemo={handleOpenDemo} />} />
              <Route path="/briefingiq" element={<BriefingIQPage onOpenDemo={handleOpenDemo} />} />
              <Route path="/eventiq" element={<EventIQPage onOpenDemo={handleOpenDemo} />} />
              <Route path="/platform" element={<PlatformPage onOpenDemo={handleOpenDemo} />} />
              <Route path="/company" element={<CompanyPage onOpenDemo={handleOpenDemo} />} />
              <Route path="/pricing" element={<PricingPage onOpenDemo={handleOpenDemo} />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/request-demo" element={<RequestDemoPage />} />
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Admin (no public layout) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog"
              element={
                <ProtectedRoute>
                  <AdminBlog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog/new"
              element={
                <ProtectedRoute>
                  <AdminBlogEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog/:id"
              element={
                <ProtectedRoute>
                  <AdminBlogEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/workflows"
              element={
                <ProtectedRoute>
                  <AdminWorkflows />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/workflows/new"
              element={
                <ProtectedRoute>
                  <AdminWorkflowNew />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/workflows/:id"
              element={
                <ProtectedRoute>
                  <AdminWorkflow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute>
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/images"
              element={
                <ProtectedRoute>
                  <AdminImages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/contacts"
              element={
                <ProtectedRoute>
                  <AdminContacts />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
        <Toaster />
      </div>
    </HelmetProvider>
  );
}

export default App;
